import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { swatchSvg } from '../lib/patterns.js';

/**
 * The hover layer every chart ships with: the underlying numbers, never a percentage of
 * something the reader cannot see. Positioned in page coordinates by whoever shows it, and
 * flipped away from the edges so it never leaves the viewport.
 *
 * `show({ x, y, title, rows, footer })` where rows are { label, value, token?, fill? }.
 */
class ChartTooltip extends LitElement {
  static properties = {
    _data: { state: true },
    _x: { state: true },
    _y: { state: true },
  };

  static styles = css`
    :host {
      position: fixed;
      z-index: 60;
      pointer-events: none;
      inset: 0 auto auto 0;
    }
    .box {
      position: absolute;
      min-width: 140px;
      max-width: 320px;
      background: var(--tooltip-bg);
      color: var(--tooltip-ink);
      border-radius: 8px;
      padding: 8px 10px;
      box-shadow: var(--shadow-menu);
      font-size: 12.5px;
      line-height: 1.45;
    }
    .title {
      font-weight: 700;
      margin-bottom: 4px;
    }
    .row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .row .label {
      opacity: 0.85;
    }
    .row .value {
      margin-left: auto;
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
    .footer {
      margin-top: 6px;
      padding-top: 6px;
      border-top: 1px solid rgba(255, 255, 255, 0.18);
      opacity: 0.85;
    }
    svg {
      display: block;
      flex: none;
    }
  `;

  /** @param {{x: number, y: number, title?: string, rows?: object[], footer?: string}} data */
  show(data) {
    this._data = data;
    this._x = data.x;
    this._y = data.y;
    this.requestUpdate();
  }

  hide() {
    this._data = null;
  }

  updated() {
    // Flip once measured, so the box never hangs off the right or bottom edge.
    const box = this.renderRoot?.querySelector('.box');
    if (!box || !this._data) return;
    const rect = box.getBoundingClientRect();
    const pad = 12;
    let left = this._data.x + 14;
    let top = this._data.y + 14;
    if (left + rect.width > window.innerWidth - pad) left = this._data.x - rect.width - 14;
    if (top + rect.height > window.innerHeight - pad) top = this._data.y - rect.height - 14;
    box.style.left = `${Math.max(pad, left)}px`;
    box.style.top = `${Math.max(pad, top)}px`;
  }

  render() {
    const data = this._data;
    if (!data) return html``;
    return html`
      <div class="box" role="tooltip">
        ${data.title ? html`<div class="title">${data.title}</div>` : ''}
        ${(data.rows ?? []).map(
          (row) => html`
            <div class="row">
              ${
                row.token
                  ? unsafeSVG(
                      swatchSvg({ slotVar: row.token, fill: row.fill ?? 'solid', size: 11 }),
                    )
                  : ''
              }
              <span class="label">${row.label}</span>
              <span class="value">${row.value}</span>
            </div>
          `,
        )}
        ${data.footer ? html`<div class="footer">${data.footer}</div>` : ''}
      </div>
    `;
  }
}

customElements.define('chart-tooltip', ChartTooltip);
